/**
 * Occlusion layers — large scenery partially covers the character.
 *
 * A prop may split into:
 *   1. Background layer — drawn behind the character (roof, canopy, …)
 *   2. Occlusion layer  — cropped lower strip drawn in front (door, trunk, …)
 *
 * Both layers share one texture. No duplicate artwork on disk.
 */

import type { SpawnPoint } from '@/world-prototype/types';

/** Crop rectangle in local offsets from the prop ground/center anchor. */
export interface OcclusionSourceRect {
  /** Left edge offset from the anchor (world-width units). */
  x: number;
  /** Top edge offset from the anchor (world-height units; negative = up). */
  y: number;
  width: number;
  height: number;
}

/** Optional walk-behind occlusion authored on a scenery prop. */
export interface OcclusionDef {
  enabled: boolean;
  /**
   * Region of the source texture to draw in front of the character.
   * Same local offset space as footprint / stand.
   */
  sourceRect: OcclusionSourceRect;
}

/** Paint depth for the non-occluding (background) half of a split prop. */
export function occlusionBackgroundDepth(propDepth: number): number {
  // Stay in the scenery band but always under a character standing near the prop.
  return Math.max(25, Math.round(propDepth - 55));
}

/** Convert a local sourceRect into a 4-point polygon (TL → TR → BR → BL). */
export function sourceRectToPolygon(
  rect: OcclusionSourceRect,
): SpawnPoint[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

/** World-space AABB for debug / editor overlays. */
export function occlusionRectWorld(
  anchor: SpawnPoint,
  local: OcclusionSourceRect,
  rotatedCorners: readonly SpawnPoint[],
): { x: number; y: number; width: number; height: number } {
  if (rotatedCorners.length === 0) {
    return {
      x: anchor.x + local.x,
      y: anchor.y + local.y,
      width: local.width,
      height: local.height,
    };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of rotatedCorners) {
    const wx = anchor.x + p.x;
    const wy = anchor.y + p.y;
    minX = Math.min(minX, wx);
    minY = Math.min(minY, wy);
    maxX = Math.max(maxX, wx);
    maxY = Math.max(maxY, wy);
  }
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
